import React, { Suspense, useState } from "react";
import "antd/dist/antd.min.css";
import "./index.css";

import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { store, persistor } from "./store/store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { LoadingOutlined } from "@ant-design/icons";
import { AuthWrapper } from "./layouts/AuthWrapper";
import FullStory from "react-fullstory";
import { ErrorBoundary } from "react-error-boundary";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { AppContext } from "./context";

import { FORM_FIELDS, MEETING_PAYLOAD } from "./data-mocks";

import Signup from "./pages/Signup";
import Login from "./pages/Login";
import Mentors from "./pages/Mentors";
import CompleteSignup from "./pages/CompleteSignup";
import ResetPassword from "./pages/ResetPassword";
import CreateCohort from "./components/cohorts/create-cohort";
import MyCohorts from "./components/cohorts/my-cohorts";
import Cohorts from "./pages/Cohort.js";
import CohortDetailsPreview from "./components/cohorts/CohortDetailsPreview";
import PreviewCohort from "./pages/PreviewCohort";

import DashboardLayout from "./layouts/DashboardLayout";
// app pages
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const Careers = React.lazy(() => import("./pages/Careers"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const Affliliates = React.lazy(() => import("./pages/Affliliates"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const StarterGuide = React.lazy(() => import("./pages/StarterGuide"));
const Mentorship = React.lazy(() => import("./pages/Mentorship"));
const TopMentors = React.lazy(() => import("./pages/TopMentors"));
const HelpCenter = React.lazy(() => import("./pages/HelpCenter"));
const VerifyTnx = React.lazy(() => import("./pages/VerifyTnx"));

const FAQ = React.lazy(() => import("./pages/FAQ"));
const Identification = React.lazy(() => import("./pages/Identification"));
const GettingStarted = React.lazy(() => import("./pages/GettingStarted"));
const BuildWithUs = React.lazy(() => import("./pages/BuildWithUs"));
const AccountManagement = React.lazy(() => import("./pages/AccountManagement"));
const QalortMentorship = React.lazy(() => import("./pages/QalortMentorship"));
const Mentoring = React.lazy(() => import("./pages/Mentoring"));
const Policies = React.lazy(() => import("./pages/Policies"));
const Earning = React.lazy(() => import("./pages/Earning"));
const Session = React.lazy(() => import("./pages/Session"));

const MentorIdentity = React.lazy(() => import("./pages/MentorIdentity"));
const RestrictedContent = React.lazy(() => import("./pages/RestrictedContent"));
const Home = React.lazy(() => import("./pages/Home"));
const Mentor = React.lazy(() => import("./pages/Mentor"));
const CohortHome = React.lazy(() => import("./pages/CohortHome"));

const NotFound = React.lazy(() => import("./pages/NotFound"));
const Wrapper = React.lazy(() => import("./layouts/AppWrapper"));
const Cohort = React.lazy(() => import("./pages/Cohort"));
const MentorsCohorts = React.lazy(() => import("./pages/MentorsCohorts"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const HelpPayment = React.lazy(() => import("./pages/HelpPayment"));
const Profile = React.lazy(() => import("./pages/Profile"));
const EditProfile = React.lazy(() => import("./pages/EditProfile"));
const QNotes = React.lazy(() => import("./pages/QNotes"));
const DashboardHome = React.lazy(() => import("./pages/DashboardHome"));
const DashboardAnalytics = React.lazy(() =>
  import("./pages/DashboardAnalytics")
);
const Calendar = React.lazy(() => import("./pages/Calendar"));
const LiveMeeting = React.lazy(() => import("./pages/LiveMeeting"));

export const queryClient = new QueryClient();

function App() {
  // const [formFields, setFormFields] = useState(FORM_FIELDS);
  const [formFields, setFormFields] = useState({});
  const [meetingPayload, setMeetingPayload] = useState(MEETING_PAYLOAD);

  const updateContext = (data) => {
    setFormFields(data);
  };

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary FallbackComponent={NotFound}>
          <Provider store={store}>
            <FullStory org="18JEZN" />
            <AppContext.Provider
              value={{
                formFields,
                updateContext,
                meetingPayload,
                setMeetingPayload,
              }}
            >
              <PersistGate loading={null} persistor={persistor}>
                <Router>
                  <Suspense
                    fallback={
                      <div
                        className="flex flex-col items-center justify-center h-screen"
                        data-testid="app_loader"
                      >
                        <LoadingOutlined style={{ fontSize: "50px" }} />
                      </div>
                    }
                  >
                    <Wrapper>
                      <div className="">
                        <Routes>
                          {/* protected routes */}
                          <Route
                            path="/profile/complete"
                            element={
                              <AuthWrapper>
                                <CompleteSignup />
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/Verify-tnx"
                            element={
                              <AuthWrapper>
                                <VerifyTnx />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/live/:slug"
                            element={
                              <AuthWrapper>
                                <LiveMeeting />
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/dashboard"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <DashboardHome />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/dashboard/analytics"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <DashboardAnalytics />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/calendar"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <Calendar />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/cohort"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <MyCohorts />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/cohort/messages/:slug"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <Cohorts />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/cohort/:cohort_id"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <MyCohorts />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/cohort/create-cohort"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <CreateCohort />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/qnotes"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <QNotes />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          <Route
                            path="/settings"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <Profile />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />
                          <Route
                            path="/settings/:which_setting"
                            element={
                              <AuthWrapper>
                                <DashboardLayout>
                                  <EditProfile />
                                </DashboardLayout>
                              </AuthWrapper>
                            }
                          />

                          {/* public routes */}
                          <Route path="/mentors" element={<Mentors />} />
                          <Route path="/ct/:slug" element={<PreviewCohort />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/login" element={<Login />} />
                          <Route
                            path="/reset-password/:passwordId"
                            element={<ResetPassword />}
                          />
                          <Route
                            path="/forgot-password"
                            element={<ForgotPassword />}
                          />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/mentorship" element={<Mentorship />} />
                          <Route path="/top-mentors" element={<TopMentors />} />
                          <Route path="/help-center" element={<HelpCenter />} />
                          <Route path="/FAQ" element={<FAQ />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route
                            path="/starter-guide"
                            element={<StarterGuide />}
                          />
                          <Route path="/career" element={<Careers />} />
                          <Route path="/affiliates" element={<Affliliates />} />
                          <Route
                            path="/build-with-us"
                            element={<BuildWithUs />}
                          />
                          <Route path="/about-us" element={<AboutUs />} />
                          <Route path="/contact-us" element={<ContactUs />} />
                          <Route
                            path="/mentor/:username"
                            element={<Mentor />}
                          />
                          <Route path="/" element={<Home />} />
                          <Route
                            path="/users/verify-email/:token"
                            element={<VerifyEmail />}
                          />
                          <Route path="*" element={<NotFound />} />
                          <Route
                            path="/gettingstarted"
                            element={<GettingStarted />}
                          />
                          <Route
                            path="/accountmanagement"
                            element={<AccountManagement />}
                          />
                          <Route
                            path="/qalortmentorship"
                            element={<QalortMentorship />}
                          />
                          <Route
                            path="/identification"
                            element={<Identification />}
                          />
                          <Route path="/mentoring" element={<Mentoring />} />
                          <Route path="/policies" element={<Policies />} />
                          <Route path="/earning" element={<Earning />} />
                          <Route path="/session" element={<Session />} />
                          <Route
                            path="/mentoridentity"
                            element={<MentorIdentity />}
                          />
                          <Route
                            path="/restrictedcontent"
                            element={<RestrictedContent />}
                          />
                          <Route
                            path="/cohorthome/:username/:slug"
                            element={<CohortHome />}
                          />
                          <Route
                            path="/helppayment"
                            element={<HelpPayment />}
                          />
                          <Route
                            path="/mentor/:username/viewcohorts"
                            element={<MentorsCohorts />}
                          />
                        </Routes>
                      </div>
                    </Wrapper>
                  </Suspense>
                </Router>
              </PersistGate>
            </AppContext.Provider>
          </Provider>
        </ErrorBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
